import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {SortTableUniqueRowId} from '../../../shared/models/sort-table/types';
import {BulkSelectCapabilities} from '../../../shared/models/bulk-select';
import {Tile, TileId, TileFormat, TILE_TYPE_SUPER_FOLDER, TILE_TYPE_SIMPLE_ANALOG, TILE_TYPE_SIMPLE_BOOL} from '../../../shared/models/tile';
import {TilesService} from '../../../core/services/tiles.service';
import {ObjectId} from '../../../shared/models/object.model';
import {Device, DeviceId} from '../../../shared/models/device';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import {DevicesService} from '../../../core/services/devices.service';
import {concat, Observable, Subscription, forkJoin, of} from 'rxjs';
import {ClickService} from '../../../core/services/click.service';
import {BulkCloneTilesComponent} from './bulk-clone-tiles/bulk-clone-tiles.component';
import { DialogBoxButtonColor } from 'src/shared/models/dialog-box';
import { BulkModalService } from '@features/tile-bulk-operations/shared/services/bulk-modal.service';
import { CSVConfigExporter } from '../../../features/tile-bulk-operations/shared/utils/csv-config-exporter.util';
import { AppsService } from '../../../core/services/apps.service';
import { catchError, map } from 'rxjs/operators';
import { FeatureFlagsService, FeatureFlag } from '@core/services/feature-flags.service';

const ERROR_CLONE_TILES_UNSUPPORTED_TYPE = `the current selection includes an unsupported tile type`;
const ERROR_CLONE_TILES_MULTIPLE_DEVICES = `cloning is only supported for a set of tiles that all depend on the same device`;

@Component({
  selector: 'motn-bulk-action-tool',
  templateUrl: './bulk-action-tool.component.html',
  styleUrls: ['./bulk-action-tool.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BulkCloneTilesComponent,
  ],
})
export class BulkActionToolComponent implements OnInit, OnDestroy {
  private clickSub: Subscription;
  @ViewChild('shade') $shadeEl;

  @Input() selectedValues: SortTableUniqueRowId[];
  @Input() capabilities: BulkSelectCapabilities;

  @Output() done$: EventEmitter<any> = new EventEmitter<any>();

  doingDeleteTiles: boolean;
  doingCloneTiles: boolean;
  tiles: Tile[];
  devices: Device[];


  get doingBulkAction(): boolean {
    return this.doingCloneTiles || this.doingDeleteTiles;
  }

  get isBulkConfigurationEnabled(): boolean {
    return this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_BULK_TILE_CONFIGURATION);
  }


  constructor(
    private tilesService: TilesService,
    private devicesService: DevicesService,
    private clickService: ClickService,
    private shadeTools: ShadeToolsService,
    private bulkModalService: BulkModalService,
    private appsService: AppsService,
    private featureFlagsService: FeatureFlagsService,
  ) {
  }


  ngOnInit(): void {
    this.clickSub = this.clickService.click$.subscribe(
      ($el) => {
        if (this.$shadeEl?.nativeElement === $el) {
          this.closeOpenTools();
        }
      },
    );
  }


  ngOnDestroy() {
    this.clickSub?.unsubscribe();
  }


  cancel() {
    this.done$.emit();
  }


  closeOpenTools() {
    this.doingCloneTiles = false;
  }


  /**
   * Call this only if you know that the selected value IDs are actually
   * object IDs. No validation happens for this automatically.
   *
   * @private
   */
  private getSelectedObjectIds(): ObjectId[] {
    return this.selectedValues.map(id => id);
  }


  /**
   * Call this only if you know that the type of objects being selected
   * by the end user are tiles. No validation happens for this
   * automatically.
   *
   * @param tileIds - Optional. Only for optimizing calls.
   * @private
   */
  private getSelectedTiles(tileIds?: TileId[]): Tile[] {
    if (!tileIds) {
      tileIds = this.getSelectedObjectIds();
    }

    const tiles: Tile[] = [];

    for (let tileId of tileIds) {
      tiles.push(this.tilesService.sessUserTiles[tileId]);
    }

    return tiles;
  }


  startDeleteTiles() {
    const tileIds: TileId[] = this.getSelectedObjectIds();

    if (tileIds.length === 0) {
      this.shadeTools.errorDialog(`You must select at least one tile to continue.`);
      return;
    }

    this.shadeTools.startDialog(
      `Delete Tiles?`,
      `Are you sure you want to delete all ${tileIds.length} selected tiles? To confirm, type DELETE in the box below and confirm.`,
      [
        {
          text: 'Confirm',
          callback: (input: string) => {
            if (input !== 'DELETE') {
              this.shadeTools.errorDialog(
                `The word DELETE must be typed exactly for deletion to occur. Please try again.`,
              );
              return;
            }

            const deletions: Observable<TileId>[] = tileIds.map(
              (tileId) => {
                return this.tilesService.deleteTile$(tileId);
              },
            );

            let successful: number = 0;

            concat(...deletions).subscribe({
              next: () => {
                successful++;
              },
              error: () => {
                this.shadeTools.errorDialog(`Bulk tile deletion failed. Before failing, ${successful} of the selected tiles were successfully deleted.`);
                this.cancel();
              },
              complete: () => {
                this.shadeTools.simpleOkDialog(`Success`, `All selected tiles were successfully deleted.`);
                this.cancel();
              },
            });
          },
          color: DialogBoxButtonColor.Red,
        },
        {
          text: 'Cancel',
          callback: () => {
            this.shadeTools.closeAll();
          },
          color: DialogBoxButtonColor.Alt,
        },
      ],
      true,
      false,
      false,
      false,
    );
  }


  startCloneTiles() {
    const tileIds: TileId[] = this.getSelectedObjectIds();
    const tiles: Tile[] = this.tiles = this.getSelectedTiles(tileIds);

    if (tiles.length === 0) {
      this.shadeTools.errorDialog(`You must select at least one tile to continue.`);
      return;
    }

    const cannotCloneReason = this.cannotCloneSelectedTiles();

    if (cannotCloneReason) {
      this.shadeTools.errorDialog(`Tile cloning is not supported for the selected tiles because ${cannotCloneReason}.`);
      return;
    }

    this.doingCloneTiles = true;
  }


  /**
   * Returns a reason why selected tiles cannot be cloned, or an empty string
   * if nothing prevents selected tiles from being cloned.
   */
  cannotCloneSelectedTiles(): string {
    const deviceIds = new Set<DeviceId>();

    for (let tile of this.getSelectedTiles()) {
      try {
        if (!tile.typeSupportsCloning()) {
          return ERROR_CLONE_TILES_UNSUPPORTED_TYPE;
        }

        if (!tile.isCanvasTile()) {
          deviceIds.add(tile.getDeviceIds()[0]);
        }
      } catch (e) {
        return ERROR_CLONE_TILES_UNSUPPORTED_TYPE;
      }
    }

    return deviceIds.size > 1 ? ERROR_CLONE_TILES_MULTIPLE_DEVICES : '';
  }


  /**
   * Returns a reason why configuration cannot be done for selected tiles,
   * or an empty string if nothing prevents it.
   */
  cannotConfigureSelectedTiles(): string {
    const tiles: Tile[] = this.getSelectedTiles();

    if (tiles.length === 0) {
      return 'no tiles selected';
    }

    // Check if any tile is a folder or formula tile
    for (let tile of tiles) {
      if (tile.Type === TILE_TYPE_SUPER_FOLDER) {
        return 'folder tiles cannot be configured';
      }
      if (tile.isFormulaTile()) {
        return 'formula tiles cannot be bulk configured';
      }
    }

    // Determine tile format from first tile
    const firstFormat = this.getTileFormat(tiles[0]);
    if (firstFormat === TileFormat.Empty) {
      return 'the selected tile type cannot be configured';
    }

    // Check all tiles are same format
    for (let tile of tiles) {
      if (this.getTileFormat(tile) !== firstFormat) {
        return 'all selected tiles must be of the same type (numeric or toggle)';
      }
    }

    return '';
  }


  startConfigureTiles() {
    const tileIds: TileId[] = this.getSelectedObjectIds();

    if (tileIds.length === 0) {
      this.shadeTools.errorDialog(`You must select at least one tile to continue.`);
      return;
    }

    const disabledReason = this.cannotConfigureSelectedTiles();
    if (disabledReason) {
      this.shadeTools.errorDialog(`Bulk configuration is not supported because ${disabledReason}.`);
      return;
    }

    // Configuration component doesn't need tileIds - users provide them via CSV upload
    this.bulkModalService.openConfigurationModal({});
    this.cancel();
  }


  startDownloadConfiguration() {
    const tileIds: TileId[] = this.getSelectedObjectIds();
    const tiles: Tile[] = this.getSelectedTiles(tileIds);

    if (tiles.length === 0) {
      this.shadeTools.errorDialog(`You must select at least one tile to continue.`);
      return;
    }

    const disabledReason = this.cannotDownloadConfigurationForSelectedTiles();
    if (disabledReason) {
      this.shadeTools.errorDialog(`Configuration download is not supported because ${disabledReason}.`);
      return;
    }

    // Fetch linked apps for all tiles in parallel
    const appFetches$ = tiles.map(tile =>
      this.appsService.getAppsLinkedToTile$(tile.ID).pipe(
        map(apps => ({ tileId: tile.ID, appIds: apps.map(app => app.ID) })),
        catchError(() => of({ tileId: tile.ID, appIds: [] }))
      )
    );

    forkJoin(appFetches$).subscribe({
      next: (results) => {
        // Build tile to app IDs map
        const tileAppMap = new Map<string, string[]>();
        results.forEach(result => {
          tileAppMap.set(result.tileId, result.appIds);
        });

        CSVConfigExporter.downloadTilesCSV({ tiles, tileAppMap }).subscribe({
          next: () => this.cancel(),
          error: (error) => {
            this.shadeTools.errorDialog(`Failed to download configuration: ${error.message}`);
          }
        });
      },
      error: (error) => {
        this.shadeTools.errorDialog(`Failed to fetch linked apps: ${error.message}`);
      }
    });
  }


  /**
   * Returns a reason why configuration cannot be downloaded for selected tiles,
   * or an empty string if nothing prevents it.
   */
  cannotDownloadConfigurationForSelectedTiles(): string {
    const tiles: Tile[] = this.getSelectedTiles();

    if (tiles.length === 0) {
      return 'no tiles selected';
    }

    // Check if any tile is a folder
    for (let tile of tiles) {
      if (tile.Type === TILE_TYPE_SUPER_FOLDER) {
        return 'folder tiles cannot be exported';
      }
    }

    // Determine tile format from first tile
    const firstFormat = this.getTileFormat(tiles[0]);
    if (firstFormat === TileFormat.Empty) {
      return 'the selected tile type cannot be exported';
    }

    // Check all tiles are same format
    for (let tile of tiles) {
      if (this.getTileFormat(tile) !== firstFormat) {
        return 'all selected tiles must be of the same type (numeric or toggle)';
      }
    }

    return '';
  }

  private getTileFormat(tile: Tile): TileFormat {
    if (tile.Type === TILE_TYPE_SUPER_FOLDER) {
      return TileFormat.Empty;
    }

    if (tile.Type === TILE_TYPE_SIMPLE_ANALOG) {
      return TileFormat.Numeric;
    }

    if (tile.Type === TILE_TYPE_SIMPLE_BOOL) {
      return TileFormat.Toggle;
    }

    return TileFormat.Empty;
  }
}
